/**
 * The screen's stylesheet, as plain CSS text the component renders in a
 * `<style>` element.
 *
 * Why a string and not a `.scss` file next to the component: this package is
 * published as SOURCE and consumed outside the monorepo, and it has no build
 * step of its own. A `.scss` import only works for a consumer whose bundler has
 * a Sass loader configured; a string needs nothing beyond React. It cannot be
 * inline `style` props either: `@keyframes`, `:nth-child` and the
 * `prefers-reduced-motion` query exist only in a stylesheet.
 *
 * Limitation: a page whose Content-Security-Policy restricts `style-src`
 * without `'unsafe-inline'` (or a nonce) blocks this element, and the screen
 * then renders unstyled. Neither Manager nor Pro Studio sends a CSP today.
 *
 * Values were compiled from the design-system tokens this screen was designed
 * with (Figma "Manager — Access Loader (Tool Card)", file f91DHgtR7vwpQGtR7zexsu,
 * section 25501:28955). The design system exposes those tokens only as Sass
 * functions, not as CSS custom properties, so the resolved values are written
 * out here with the token named beside each one. A token change in the design
 * system therefore does NOT reach this file on its own.
 */
export const ACCESS_SETUP_SCREEN_CSS = `
/* Figma "Screen (main)": a 24 / 48 padded column, content centred. The design
   draws the 60px nav OVER the top of an 860px screen, so the view is centred in
   the whole frame; here the page body starts below the nav, so those 60px are
   added to the bottom padding, which moves the centre of the content box up by
   30px — the design's centre line at any viewport height. */
.c-access-setup {
  box-sizing: border-box;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* spacing(2xlarge) spacing(large) spacing(2xlarge) + 60px nav */
  padding: 48px 24px 108px;
}

/* On the page body, not on .c-access-setup: the body spans the page while its
   wrapper caps the content width, which would leave seams on either side. No
   design-system token matches these two stops. */
.c-access-setup-page {
  background: linear-gradient(to bottom, #f4f2fc, #f7f6fb);
}

/* The shared frame: media 220 · copy (headline 36 + 8 + body 28) · gap 32 · actions 48. */
.c-access-setup__view {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.c-access-setup__media {
  width: 220px;
  height: 220px;
  display: grid;
  place-items: center;
}

.c-access-setup__copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px; /* spacing(xsmall) */
  max-width: 600px;
}

/* H4 — font-family(sans), font-size(medium) 24/36, font-weight(medium), color(gray, 500). */
.c-access-setup__headline {
  margin: 0;
  min-height: 36px;
  font-family: Gordita, Poppins, sans-serif;
  font-size: 24px;
  line-height: 36px;
  font-weight: 500;
  color: #151a25;
  text-wrap: balance;
}

/* Loading keeps the 36px headline row empty so the body sits on the same line
   as in the failed/denied states (Figma node 25501:28962). */
.c-access-setup__headline--empty {
  width: 1px;
}

/* Subtitle 1 — font-size(xsmall) 16 with a literal 28px line-height (no token
   has 16/28), font-weight(medium), color(gray, 400). */
.c-access-setup__body {
  margin: 0;
  min-height: 28px;
  font-family: Gordita, Poppins, sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: 500;
  color: #62697c;
}

/* The ellipsis counts . -> .. -> ... on a loop; every dot keeps its space so the
   centred sentence never shifts. */
.c-access-setup__dots span:nth-child(2) {
  animation: access-dot-2 1.5s steps(1, end) infinite;
}

.c-access-setup__dots span:nth-child(3) {
  animation: access-dot-3 1.5s steps(1, end) infinite;
}

.c-access-setup__actions {
  min-height: 48px;
  margin-top: 32px; /* spacing(xlarge) */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px; /* spacing(medium) */
  flex-wrap: wrap;
}

/* Setup note — 14/20 (no token), font-weight(normal), color(gray, 400). */
.c-access-setup__note {
  margin: 0;
  font-family: Gordita, Poppins, sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #62697c;
}

.c-access-setup__illustration {
  width: 179px;
  height: 143px;
  display: block;
}

/* Tool loader card (Figma "Access/Tool loader card"): 220x220, every layer
   centred. Radii, stroke widths and offsets are illustration geometry. */
.c-access-setup__card-stage {
  position: relative;
  width: 220px;
  height: 220px;
  display: grid;
  place-items: center;
}

/* Radial color(purple, 300) @ 0.22 -> 0 at 70%, node opacity 0.6. */
.c-access-setup__glow {
  position: absolute;
  width: 184px;
  height: 184px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(133, 115, 231, 0.22) 0%, rgba(133, 115, 231, 0) 70%);
  opacity: 0.6;
  animation: access-glow 4.6s ease-in-out infinite;
}

.c-access-setup__ripple {
  position: absolute;
  width: 154px;
  height: 154px;
  border-radius: 38px;
  border: 1px solid rgba(133, 115, 231, 0.4); /* color(purple, 300) @ 0.4 */
  opacity: 0.4;
  animation: access-ripple 5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}

.c-access-setup__ripple--late {
  animation-delay: 2.5s;
}

.c-access-setup__card-wrap {
  position: relative;
  z-index: 1;
  width: 140px;
  height: 140px;
  animation: access-breathe 4s ease-in-out 1s infinite;
}

/* color(base, white) -> color(purple, 50); the shadow colour #736d9d has no token. */
.c-access-setup__card {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  border-radius: 35px;
  background: linear-gradient(155.22deg, hsl(0, 0%, 100%) 12.03%, #faf9fe 87.22%);
  border: 1px solid rgba(133, 115, 231, 0.14);
  box-shadow: 0 4px 10px rgba(115, 109, 157, 0.12), 0 16px 40px -8px rgba(115, 109, 157, 0.32);
  overflow: hidden;
}

.c-access-setup__card-logo {
  width: 82px;
  height: 82px;
  display: block;
  transition: opacity 0.4s ease;
}

.c-access-setup__ring {
  position: absolute;
  inset: 0;
  overflow: visible;
  pointer-events: none;
}

.c-access-setup__ring-track {
  fill: none;
  stroke: rgba(133, 115, 231, 0.2); /* color(purple, 300) @ 0.2 */
  stroke-width: 2.5;
}

.c-access-setup__ring-trace {
  fill: none;
  stroke: #8573e7; /* color(purple, 300) */
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 1 1;
  stroke-dashoffset: 1;
  animation: access-ring-trace 4.6s linear infinite;
}

/* Failed: motion stops and the logo is drawn with the luminosity blend mode at
   55% opacity (Figma "Tool loader card — Failed", node 25501:28971). */
.c-access-setup__card-stage--failed .c-access-setup__glow,
.c-access-setup__card-stage--failed .c-access-setup__ripple {
  animation: none;
  opacity: 0;
}

.c-access-setup__card-stage--failed .c-access-setup__card-wrap {
  animation: none;
}

.c-access-setup__card-stage--failed .c-access-setup__ring-trace {
  animation: none;
  opacity: 0;
}

.c-access-setup__card-stage--failed .c-access-setup__card-logo {
  mix-blend-mode: luminosity;
  opacity: 0.55;
}

@keyframes access-breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.045); }
}

@keyframes access-glow {
  0%, 100% { transform: scale(1); opacity: 0.45; }
  50% { transform: scale(1.12); opacity: 0.72; }
}

@keyframes access-ripple {
  0% { transform: scale(0.62); opacity: 0; }
  18% { opacity: 0.4; }
  100% { transform: scale(1.32); opacity: 0; }
}

@keyframes access-ring-trace {
  0% { stroke-dashoffset: 1; }
  100% { stroke-dashoffset: -1; }
}

@keyframes access-dot-2 {
  0% { opacity: 0; }
  33.33% { opacity: 1; }
}

@keyframes access-dot-3 {
  0% { opacity: 0; }
  66.66% { opacity: 1; }
}

/* Every decorative animation stops, the ring trace included. The loading state
   is still conveyed by the body copy and aria-busy. */
@media (prefers-reduced-motion: reduce) {
  .c-access-setup__glow,
  .c-access-setup__ripple,
  .c-access-setup__card-wrap,
  .c-access-setup__dots span,
  .c-access-setup__ring-trace {
    animation: none !important;
  }
}
`;
